import React, { useState } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Upload, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const BrandingPage = () => {
  const { school, refreshSchool } = useAuth();
  const [schoolName, setSchoolName] = useState(school?.name || '');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveName = async () => {
    setSaving(true);
    try {
      await api.put('/school/branding', { school_name: schoolName });
      await refreshSchool();
      toast.success('School name updated successfully');
    } catch (err) {
      toast.error('Failed to update school name');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) { toast.error('Please select a logo file'); return; }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      await api.post('/school/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshSchool();
      setLogoFile(null);
      setLogoPreview(null);
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setRemovingLogo(true);
    try {
      await api.delete('/school/logo');
      await refreshSchool();
      setLogoFile(null);
      setLogoPreview(null);
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    } finally {
      setRemovingLogo(false);
    }
  };

  const hasCurrentLogo = !!school?.logo_path;
  const showLogoPreviewCard = hasCurrentLogo || logoPreview;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">School Branding</h1>
          <p className="text-slate-500">Customize your school's appearance</p>
        </div>

        {/* School Name */}
        <Card>
          <CardHeader>
            <CardTitle>School Name</CardTitle>
            <CardDescription>
              This name appears in the header across all pages
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="schoolName">School Name</Label>
              <Input
                id="schoolName"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="Enter school name (leave empty to hide)"
                data-testid="school-name-input"
              />
            </div>
            <Button onClick={handleSaveName} disabled={saving} data-testid="save-school-name-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Save Name
            </Button>
          </CardContent>
        </Card>

        {/* Current Logo — only visible when a logo is uploaded */}
        {showLogoPreviewCard && (
          <Card>
            <CardHeader>
              <CardTitle>Current Logo</CardTitle>
              <CardDescription>Your school logo shown in the header</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative inline-block">
                <img
                  src={
                    logoPreview ||
                    `${process.env.REACT_APP_BACKEND_URL || ''}/api/school/logo/${school.logo_path}`
                  }
                  alt="School logo"
                  className="h-24 w-24 rounded-xl object-cover border border-slate-200 dark:border-slate-700"
                />
                {/* X remove button — only on the saved logo (not the local preview) */}
                {hasCurrentLogo && !logoPreview && (
                  <button
                    onClick={handleRemoveLogo}
                    disabled={removingLogo}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-md transition-colors disabled:opacity-60"
                    title="Remove logo"
                    data-testid="remove-logo-btn"
                  >
                    {removingLogo
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <X className="h-3.5 w-3.5" />
                    }
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Logo */}
        <Card>
          <CardHeader>
            <CardTitle>School Logo</CardTitle>
            <CardDescription>
              {hasCurrentLogo
                ? 'Upload a new logo to replace the current one'
                : 'Upload your school logo (recommended size: 200×200px)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              data-testid="logo-file-input"
              className="cursor-pointer"
            />
            {logoFile && (
              <Button onClick={handleUploadLogo} disabled={uploadingLogo} data-testid="upload-logo-btn">
                {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload Logo
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BrandingPage;
